import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Card, Form, InputGroup, Button, Badge, Dropdown } from 'react-bootstrap'
import LogExportModal from './LogExportModal'
import '../styles/LogViewer.css'

const getAccountKeyFromLog = (log) => {
  if (log.metadata?.accountKey) return log.metadata.accountKey
  const message = String(log.message || '')
  const match = message.match(/^\[([^\]]+)\]/)
  return match?.[1] || ''
}

const getTaskKeyFromLog = (log, taskLookup) => {
  if (log.metadata?.taskId) return log.metadata.taskId
  const message = String(log.message || '')
  const taskWithId = message.match(/任务\s+.+?\s+\(([^)]+)\)/)
  if (taskWithId?.[1]) return taskWithId[1]

  const missingTask = message.match(/任务\s+([a-zA-Z0-9_:-]+)\s+不存在/)
  if (missingTask?.[1]) return missingTask[1]

  for (const task of taskLookup.byNameLengthDesc) {
    if (message.includes(task.name) || message.includes(task.id)) {
      return task.id
    }
  }
  return ''
}

const VirtualLogViewer = ({
  logs = [],
  onClearLogs,
  exportLogs,
  getLogStats,
  accountOptions = [],
  taskOptions = []
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [filterLevel, setFilterLevel] = useState('all')
  const [filterAccount, setFilterAccount] = useState('all')
  const [filterTask, setFilterTask] = useState('all')
  const [itemHeight] = useState(24) // 每个日志条目的高度
  const [containerHeight] = useState(300) // 容器高度
  const [scrollTop, setScrollTop] = useState(0)
  const [showExportModal, setShowExportModal] = useState(false)
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true) // 自动滚动开关
  const [isUserScrolling, setIsUserScrolling] = useState(false) // 用户是否在主动滚动
  
  const scrollElementRef = useRef(null)
  const lastLogCount = useRef(logs.length) // 初始化为当前日志数量
  const scrollTimeout = useRef(null)
  const [isAtTop, setIsAtTop] = useState(false)
  const [isAtBottom, setIsAtBottom] = useState(true)
  const lastScrollTop = useRef(0) // 记录上次滚动位置
  const scrollDirection = useRef(0) // 滚动方向：1向下，-1向上，0无滚动
  const userInteractionRef = useRef(false) // 用户是否有真实交互

  const accountLabelMap = useMemo(() => {
    return new Map(accountOptions.map(option => [option.value, option.label]))
  }, [accountOptions])

  const taskLookup = useMemo(() => {
    const byId = new Map(taskOptions.map(option => [option.value, option]))
    const byNameLengthDesc = taskOptions
      .map(option => ({ id: option.value, name: option.label }))
      .filter(task => task.id && task.name)
      .sort((a, b) => b.name.length - a.name.length)
    return { byId, byNameLengthDesc }
  }, [taskOptions])

  const logsWithDimensions = useMemo(() => {
    return logs.map(log => ({
      ...log,
      accountKey: getAccountKeyFromLog(log),
      taskKey: getTaskKeyFromLog(log, taskLookup)
    }))
  }, [logs, taskLookup])

  const textAndLevelFilteredLogs = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase()
    return logsWithDimensions.filter(log => {
      const matchesSearch = searchTerm === '' || 
        String(log.message || '').toLowerCase().includes(normalizedSearchTerm) ||
        String(log.timestamp || '').includes(searchTerm)
      
      const matchesLevel = filterLevel === 'all' || log.type === filterLevel
      
      return matchesSearch && matchesLevel
    })
  }, [logsWithDimensions, searchTerm, filterLevel])

  const accountStats = useMemo(() => {
    const stats = new Map()
    textAndLevelFilteredLogs.forEach(log => {
      if (filterTask !== 'all' && log.taskKey !== filterTask) return
      if (!log.accountKey) return
      stats.set(log.accountKey, (stats.get(log.accountKey) || 0) + 1)
    })
    return stats
  }, [textAndLevelFilteredLogs, filterTask])

  const taskStats = useMemo(() => {
    const stats = new Map()
    textAndLevelFilteredLogs.forEach(log => {
      if (filterAccount !== 'all' && log.accountKey !== filterAccount) return
      if (!log.taskKey) return
      stats.set(log.taskKey, (stats.get(log.taskKey) || 0) + 1)
    })
    return stats
  }, [textAndLevelFilteredLogs, filterAccount])

  const accountFilterOptions = useMemo(() => {
    const knownOptions = accountOptions.filter(option => accountStats.has(option.value))
    const knownValues = new Set(knownOptions.map(option => option.value))
    const discoveredOptions = Array.from(accountStats.keys())
      .filter(value => !knownValues.has(value))
      .sort()
      .map(value => ({ value, label: value }))
    return [...knownOptions, ...discoveredOptions]
  }, [accountOptions, accountStats])

  const taskFilterOptions = useMemo(() => {
    const knownOptions = taskOptions.filter(option => taskStats.has(option.value))
    const knownValues = new Set(knownOptions.map(option => option.value))
    const discoveredOptions = Array.from(taskStats.keys())
      .filter(value => !knownValues.has(value))
      .sort()
      .map(value => ({ value, label: value }))
    return [...knownOptions, ...discoveredOptions]
  }, [taskOptions, taskStats])

  // 过滤和搜索日志
  const filteredLogs = useMemo(() => {
    return textAndLevelFilteredLogs.filter(log => {
      const matchesAccount = filterAccount === 'all' || log.accountKey === filterAccount
      const matchesTask = filterTask === 'all' || log.taskKey === filterTask
      return matchesAccount && matchesTask
    })
  }, [textAndLevelFilteredLogs, filterAccount, filterTask])

  // 计算可见范围
  const visibleRange = useMemo(() => {
    const startIndex = Math.floor(scrollTop / itemHeight)
    const endIndex = Math.min(
      startIndex + Math.ceil(containerHeight / itemHeight) + 1,
      filteredLogs.length
    )
    return { startIndex: Math.max(0, startIndex), endIndex }
  }, [scrollTop, itemHeight, containerHeight, filteredLogs.length])

  // 获取可见的日志条目
  const visibleLogs = useMemo(() => {
    return filteredLogs.slice(visibleRange.startIndex, visibleRange.endIndex)
  }, [filteredLogs, visibleRange])

  useEffect(() => {
    setScrollTop(0)
    if (scrollElementRef.current) {
      scrollElementRef.current.scrollTop = 0
    }
  }, [searchTerm, filterLevel, filterAccount, filterTask])

  // 处理滚动事件
  const handleScroll = useCallback((e) => {
    const element = e.target
    const scrollTop = element.scrollTop
    const scrollHeight = element.scrollHeight
    const clientHeight = element.clientHeight
    
    setScrollTop(scrollTop)
    
    // 计算滚动方向和距离
    const scrollDelta = scrollTop - lastScrollTop.current
    const scrollDistance = Math.abs(scrollDelta)
    
    // 只有滚动距离超过阈值才认为是有意义的滚动
    if (scrollDistance > 5) {
      scrollDirection.current = scrollDelta > 0 ? 1 : -1
      lastScrollTop.current = scrollTop
      
      // 标记为用户交互（只有在滚动距离足够大时）
      if (scrollDistance > 20) {
        userInteractionRef.current = true
      }
    }
    
    // 检测滚动位置
    const atTop = scrollTop <= 10
    const atBottom = scrollTop + clientHeight >= scrollHeight - 30
    
    setIsAtTop(atTop)
    setIsAtBottom(atBottom)
    
    // 只有在用户有真实交互的情况下才改变自动滚动状态
    if (userInteractionRef.current) {
      if (atBottom && scrollDirection.current >= 0) {
        // 用户滚动到底部，启用自动滚动
        setIsAutoScrollEnabled(true)
        setIsUserScrolling(false)
        lastLogCount.current = logs.length
        userInteractionRef.current = false // 重置交互标记
      } else if (!atBottom && scrollDirection.current < 0) {
        // 用户向上滚动且不在底部，禁用自动滚动
        setIsAutoScrollEnabled(false)
        setIsUserScrolling(true)
      }
    }
    
    // 清除之前的定时器
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current)
    }
    
    // 设置定时器，重置用户交互状态
    scrollTimeout.current = setTimeout(() => {
      setIsUserScrolling(false)
      userInteractionRef.current = false
    }, 3000) // 3秒后重置状态
  }, [logs.length])

  // 智能自动滚动到底部
  useEffect(() => {
    if (scrollElementRef.current && isAutoScrollEnabled && !isUserScrolling) {
      const element = scrollElementRef.current
      const isNearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 50
      
      // 情况1：有新日志且满足条件时自动滚动
      if (
        searchTerm === '' && 
        filterLevel === 'all' && 
        filterAccount === 'all' &&
        filterTask === 'all' &&
        logs.length > lastLogCount.current &&
        (isNearBottom || !userInteractionRef.current)
      ) {
        setTimeout(() => {
          if (scrollElementRef.current && isAutoScrollEnabled && !isUserScrolling) {
            scrollElementRef.current.scrollTo({
              top: scrollElementRef.current.scrollHeight,
              behavior: 'smooth'
            })
            userInteractionRef.current = false
          }
        }, 100)
      }
    }
    
    // 更新日志数量记录
    const updateTimer = setTimeout(() => {
      lastLogCount.current = logs.length
    }, 200)
    
    return () => clearTimeout(updateTimer)
  }, [logs, searchTerm, filterLevel, filterAccount, filterTask, isAutoScrollEnabled, isUserScrolling])

  // 监听自动滚动状态变化，立即滚动到底部
  useEffect(() => {
    if (isAutoScrollEnabled && scrollElementRef.current && !isUserScrolling) {
      const element = scrollElementRef.current
      const isAtBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 30
      
      // 如果启用自动滚动但不在底部，立即滚动到底部
      if (!isAtBottom) {
        setTimeout(() => {
          if (scrollElementRef.current && isAutoScrollEnabled) {
            scrollElementRef.current.scrollTo({
              top: scrollElementRef.current.scrollHeight,
              behavior: 'smooth'
            })
          }
        }, 50)
      }
    }
  }, [isAutoScrollEnabled, isUserScrolling])

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current)
      }
    }
  }, [])

  // 获取日志类型的颜色
  const getLogTypeColor = (type) => {
    switch (type) {
      case 'error': return 'text-danger'
      case 'warning': return 'text-warning'
      case 'success': return 'text-success'
      case 'info': return 'text-info'
      default: return 'text-muted'
    }
  }

  // 获取日志类型统计
  const logStats = useMemo(() => {
    const stats = { all: logs.length, error: 0, warning: 0, success: 0, info: 0 }
    logs.forEach(log => {
      if (stats[log.type] !== undefined) {
        stats[log.type]++
      }
    })
    return stats
  }, [logs])

  if (logs.length === 0) return null

  return (
    <Card className="mb-4">
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">执行日志</h5>
          <div className="d-flex gap-2">
            <Badge bg="secondary">{filteredLogs.length}/{logs.length}</Badge>
            {exportLogs && getLogStats && (
              <Button 
                variant="outline-info" 
                size="sm" 
                onClick={() => setShowExportModal(true)}
              >
                导出
              </Button>
            )}
            {onClearLogs && (
              <Button variant="outline-secondary" size="sm" onClick={onClearLogs}>
                清空日志
              </Button>
            )}
          </div>
        </div>
        
        {/* 搜索和过滤控件 */}
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <InputGroup size="sm" style={{ maxWidth: '300px' }}>
            <Form.Control
              type="text"
              placeholder="搜索日志..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <Button 
                variant="outline-secondary" 
                onClick={() => setSearchTerm('')}
                style={{ borderLeft: 'none' }}
              >
                ×
              </Button>
            )}
          </InputGroup>
          
          <Dropdown>
            <Dropdown.Toggle variant="outline-secondary" size="sm">
              级别: {filterLevel === 'all' ? '全部' : filterLevel}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setFilterLevel('all')}>
                全部 <Badge bg="secondary" className="ms-2">{logStats.all}</Badge>
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setFilterLevel('error')}>
                错误 <Badge bg="danger" className="ms-2">{logStats.error}</Badge>
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setFilterLevel('warning')}>
                警告 <Badge bg="warning" className="ms-2">{logStats.warning}</Badge>
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setFilterLevel('success')}>
                成功 <Badge bg="success" className="ms-2">{logStats.success}</Badge>
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setFilterLevel('info')}>
                信息 <Badge bg="info" className="ms-2">{logStats.info}</Badge>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          <Form.Select
            size="sm"
            value={filterAccount}
            onChange={(e) => setFilterAccount(e.target.value)}
            style={{ maxWidth: '260px' }}
            aria-label="按账号筛选日志"
          >
            <option value="all">账号: 全部</option>
            {accountFilterOptions.map(option => (
              <option key={option.value} value={option.value}>
                {accountLabelMap.get(option.value) || option.label} ({accountStats.get(option.value) || 0})
              </option>
            ))}
          </Form.Select>

          <Form.Select
            size="sm"
            value={filterTask}
            onChange={(e) => setFilterTask(e.target.value)}
            style={{ maxWidth: '260px' }}
            aria-label="按任务筛选日志"
          >
            <option value="all">任务: 全部</option>
            {taskFilterOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label} ({taskStats.get(option.value) || 0})
              </option>
            ))}
          </Form.Select>

          {(filterAccount !== 'all' || filterTask !== 'all') && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                setFilterAccount('all')
                setFilterTask('all')
              }}
            >
              重置维度
            </Button>
          )}

          {/* 滚动控制按钮 */}
          <div className="d-flex gap-1">
            <Button
              variant={isAutoScrollEnabled ? "success" : "outline-secondary"}
              size="sm"
              onClick={() => {
                const newAutoScrollState = !isAutoScrollEnabled
                setIsAutoScrollEnabled(newAutoScrollState)
                
                // 重置用户交互状态，防止误判
                userInteractionRef.current = false
                setIsUserScrolling(false)
                
                if (newAutoScrollState && scrollElementRef.current) {
                  // 如果启用自动滚动，立即滚动到底部
                  setTimeout(() => {
                    if (scrollElementRef.current) {
                      scrollElementRef.current.scrollTo({
                        top: scrollElementRef.current.scrollHeight,
                        behavior: 'smooth'
                      })
                      lastLogCount.current = logs.length
                    }
                  }, 50)
                }
              }}
              title={isAutoScrollEnabled ? "点击暂停自动滚动" : "点击启用自动滚动"}
            >
              {isAutoScrollEnabled ? "🔄" : "⏸️"}
            </Button>
            
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                if (scrollElementRef.current) {
                  // 重置所有状态
                  userInteractionRef.current = false
                  setIsUserScrolling(false)
                  setIsAutoScrollEnabled(true)
                  
                  scrollElementRef.current.scrollTo({
                    top: scrollElementRef.current.scrollHeight,
                    behavior: 'smooth'
                  })
                  lastLogCount.current = logs.length
                }
              }}
              title="滚动到底部并启用自动滚动"
            >
              ⬇️
            </Button>
          </div>
        </div>
      </Card.Header>
      
      <Card.Body className="p-0" style={{ position: 'relative' }}>
        {/* 滚动状态指示器 */}
        {!isAutoScrollEnabled && (
          <div 
            className="position-absolute bg-warning text-dark px-2 py-1 rounded-pill small"
            style={{ 
              top: '10px', 
              right: '10px', 
              zIndex: 10,
              fontSize: '11px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}
          >
            📌 已暂停自动滚动
          </div>
        )}
        
        {/* 新日志提示 */}
        {!isAutoScrollEnabled && logs.length > lastLogCount.current && (
          <div 
            className="position-absolute bg-info text-white px-2 py-1 rounded-pill small new-logs-indicator"
            style={{ 
              bottom: '10px', 
              right: '10px', 
              zIndex: 10,
              fontSize: '11px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              cursor: 'pointer'
            }}
            onClick={() => {
              if (scrollElementRef.current) {
                // 重置所有状态
                userInteractionRef.current = false
                setIsUserScrolling(false)
                setIsAutoScrollEnabled(true)
                
                scrollElementRef.current.scrollTo({
                  top: scrollElementRef.current.scrollHeight,
                  behavior: 'smooth'
                })
                lastLogCount.current = logs.length
              }
            }}
            title="点击查看新日志"
          >
            📥 有新日志 ({logs.length - lastLogCount.current})
          </div>
        )}

        {/* 快速导航按钮 */}
        {logs.length > 50 && (
          <div 
            className="position-absolute d-flex flex-column gap-1"
            style={{ 
              top: '50px', 
              right: '10px', 
              zIndex: 10
            }}
          >
            {!isAtTop && (
              <Button
                variant="outline-secondary"
                size="sm"
                className="scroll-control-btn"
                onClick={() => {
                  if (scrollElementRef.current) {
                    // 标记为手动操作，暂时禁用自动滚动
                    userInteractionRef.current = false
                    setIsUserScrolling(false)
                    setIsAutoScrollEnabled(false)
                    
                    scrollElementRef.current.scrollTo({
                      top: 0,
                      behavior: 'smooth'
                    })
                  }
                }}
                title="跳转到顶部"
                style={{ 
                  width: '32px', 
                  height: '32px',
                  fontSize: '12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(4px)'
                }}
              >
                ⬆️
              </Button>
            )}
            
            {!isAtBottom && (
              <Button
                variant="outline-secondary"
                size="sm"
                className="scroll-control-btn"
                onClick={() => {
                  if (scrollElementRef.current) {
                    // 重置所有状态
                    userInteractionRef.current = false
                    setIsUserScrolling(false)
                    setIsAutoScrollEnabled(true)
                    
                    scrollElementRef.current.scrollTo({
                      top: scrollElementRef.current.scrollHeight,
                      behavior: 'smooth'
                    })
                    lastLogCount.current = logs.length
                  }
                }}
                title="跳转到底部"
                style={{ 
                  width: '32px', 
                  height: '32px',
                  fontSize: '12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(4px)'
                }}
              >
                ⬇️
              </Button>
            )}
          </div>
        )}

        <div 
          ref={scrollElementRef}
          className={`log-container ${isUserScrolling ? 'user-scrolling' : ''} ${isAtTop ? 'scrolled-top' : ''} ${isAtBottom ? 'scrolled-bottom' : ''}`}
          style={{ 
            height: `${containerHeight}px`, 
            overflow: 'auto',
            position: 'relative'
          }}
          onScroll={handleScroll}
        >
          {/* 虚拟滚动容器 */}
          <div style={{ height: `${filteredLogs.length * itemHeight}px`, position: 'relative' }}>
            <div 
              style={{ 
                transform: `translateY(${visibleRange.startIndex * itemHeight}px)`,
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0
              }}
            >
              {visibleLogs.map(log => (
                <div 
                  key={log.id}
                  className={`log-entry ${getLogTypeColor(log.type)}`}
                  style={{ 
                    height: `${itemHeight}px`,
                    padding: '2px 12px',
                    fontSize: '12px',
                    borderBottom: '1px solid #f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                  title={`[${log.timestamp}] ${log.message}`}
                >
                  <span className="log-timestamp me-2">
                    [{log.timestamp.split(' ')[1]}]
                  </span>
                  <span className={`badge log-level-badge bg-${log.type === 'error' ? 'danger' : log.type === 'warning' ? 'warning' : log.type === 'success' ? 'success' : 'info'} me-2`}>
                    {log.type.toUpperCase()}
                  </span>
                  <span className="log-message">{log.message}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card.Body>

      {/* 日志导出模态框 */}
      {exportLogs && getLogStats && (
        <LogExportModal
          show={showExportModal}
          onHide={() => setShowExportModal(false)}
          logs={logs}
          exportLogs={exportLogs}
          getLogStats={getLogStats}
        />
      )}
    </Card>
  )
}

export default VirtualLogViewer
